import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, Input, Label, Select, ValidationMessage } from '@digdir/designsystemet-react';
import type { ProtocolView } from '../../../api/safetyRounds';
import { useChangeProtocolDeviationMutation } from '../../../hooks/queries/safetyRounds/useRoundQueries';

type Change = NonNullable<ProtocolView['deviationsToChange']>[number];

/**
 * Before signing, the general manager gives a deviation of the protocol another responsible or deadline. It is saved
 * at once, and the deviation goes out to the responsible it has when the general manager signs.
 */
export const DeviationChangeFields = ({
  roundId,
  number,
  change,
  people,
  className,
  errorClassName,
  labels = 'hidden',
}: {
  roundId: number;
  number: string;
  change: Change;
  people: NonNullable<ProtocolView['people']>;
  className?: string;
  errorClassName?: string;
  /** The labels are read aloud only, or shown above the fields */
  labels?: 'hidden' | 'visible';
}): ReactElement => {
  const { t } = useTranslation();
  const K = 'safetyRounds.tasks.generalManager';
  const save = useChangeProtocolDeviationMutation(roundId);
  const responsibleId = `dl-responsible-${change.itemId}`;
  const dueId = `dl-due-${change.itemId}`;
  const labelClass = labels === 'hidden' ? 'ds-sr-only' : undefined;

  const send = (next: { responsibleId?: number; dueDate?: string }) => {
    const responsible = next.responsibleId ?? change.responsibleId;
    const dueDate = next.dueDate ?? change.dueDate;
    if (responsible === null || !dueDate) return;
    save.mutate({ deviationId: change.deviationId, responsibleId: responsible, dueDate });
  };

  return (
    <div className={className}>
      <Field>
        <Label htmlFor={responsibleId} className={labelClass} data-size="sm">
          {labels === 'hidden' ? (
            t(`${K}.responsibleFor`, { number })
          ) : (
            <>
              {t('safetyRounds.protocol.responsibleLabel')}
              <span className="ds-sr-only"> {number}</span>
            </>
          )}
        </Label>
        <Select
          id={responsibleId}
          data-size="sm"
          value={change.responsibleId ?? ''}
          aria-busy={save.isPending || undefined}
          onChange={(event) => send({ responsibleId: Number(event.target.value) })}
        >
          {change.responsibleId === null && (
            <Select.Option value="" disabled>
              {t(`${K}.chooseResponsible`)}
            </Select.Option>
          )}
          {people.map((person) => (
            <Select.Option key={person.userId} value={person.userId}>
              {person.email}
            </Select.Option>
          ))}
        </Select>
      </Field>
      <Field>
        <Label htmlFor={dueId} className={labelClass} data-size="sm">
          {labels === 'hidden' ? (
            t(`${K}.dueFor`, { number })
          ) : (
            <>
              {t('safetyRounds.protocol.dueLabel')}
              <span className="ds-sr-only"> {number}</span>
            </>
          )}
        </Label>
        <Input
          id={dueId}
          data-size="sm"
          type="date"
          value={change.dueDate}
          onChange={(event) => event.target.value && send({ dueDate: event.target.value })}
        />
      </Field>
      {save.isError && (
        <ValidationMessage data-size="sm" className={errorClassName}>
          {t('hms.errors.save')}
        </ValidationMessage>
      )}
    </div>
  );
};
